/**
 * Marketing landing page – showcases app features and publicly available tournament scores.
 * Inline login in header; no separate login page needed for basic sign-in.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as readApi from '../api/readApi';
import { formatDate } from '../utils/dateFormat';
import { GAMES, getGameById } from '../constants/games';
import { checkRateLimitTiered, RATE_LIMIT_PRESETS } from '../utils/rateLimit';
import { useLiveMatches } from '../hooks/useLiveMatches';
import GoogleAd from '../components/GoogleAd';
import LoadingBlock from '../components/LoadingBlock';
import './Landing.css';

function formatScoreSummary(scores) {
  if (!scores) return '—';
  const games = [];
  if (scores.game1) games.push(`${scores.game1.player1 ?? 0}-${scores.game1.player2 ?? 0}`);
  if (scores.game2) games.push(`${scores.game2.player1 ?? 0}-${scores.game2.player2 ?? 0}`);
  if (scores.game3) games.push(`${scores.game3.player1 ?? 0}-${scores.game3.player2 ?? 0}`);
  return games.length ? games.join(', ') : '—';
}

function getPlayerLabel(match) {
  const isDoubles = match?.matchType === 'doubles';
  const p1 = isDoubles
    ? (match.team1Name || (match.team1Players || []).join(' & ') || 'Team 1')
    : (match.player1Name || match.player1Id || 'Player 1');
  const p2 = isDoubles
    ? (match.team2Name || (match.team2Players || []).join(' & ') || 'Team 2')
    : (match.player2Name || match.player2Id || 'Player 2');
  return { p1, p2 };
}

function getGameAccent(gameId) {
  const map = {
    'table-tennis': 'var(--game-table-tennis)',
    pickleball: 'var(--game-pickleball)',
    badminton: 'var(--game-badminton)',
    football: 'var(--game-football)',
    cricket: 'var(--game-cricket)',
    volleyball: 'var(--game-volleyball)',
    basketball: 'var(--game-basketball)',
    tennis: 'var(--game-tennis)',
  };
  return map[gameId] || 'var(--primary)';
}

const FEATURES = [
  { icon: '🏆', title: 'Tournaments', desc: 'Create & run open or private tournaments with flexible formats.' },
  { icon: '📊', title: 'Live Scoring', desc: 'Score matches in real time; players see updates instantly.' },
  { icon: '📈', title: 'Leaderboards', desc: 'Rankings, stats and match history per sport.' },
  { icon: '📍', title: 'Venue Booking', desc: 'Reserve courts and manage availability.' },
  { icon: '📅', title: 'Smart Scheduling', desc: 'Auto-schedule matches based on player availability.' },
  { icon: '🔗', title: 'Shareable Links', desc: 'Public pages for tournaments and matches.' },
];

export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithGoogle, checkOnboardingStatus, currentUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gameFilter, setGameFilter] = useState('table-tennis');
  const [rateLimitMsg, setRateLimitMsg] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [pendingReturnTo, setPendingReturnTo] = useState(null);

  const returnTo = pendingReturnTo || location.state?.returnTo;

  useEffect(() => {
    try {
      if (sessionStorage.getItem('loginBlockedMessage')) {
        sessionStorage.removeItem('loginBlockedMessage');
        setLoginError('Logins from your user are blocked by admin.');
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (!currentUser) return;
    checkOnboardingStatus(currentUser.uid).then((onboarded) => {
      setLoginLoading(false);
      if (!onboarded) navigate('/onboarding');
      else if (returnTo) navigate(returnTo, { replace: true });
      else navigate('/dashboard');
    });
  }, [currentUser, checkOnboardingStatus, navigate, returnTo]);

  async function startGoogleSignIn(returnToPath = null) {
    if (returnToPath) setPendingReturnTo(returnToPath);
    setLoginError('');
    setLoginLoading(true);
    try {
      await loginWithGoogle();
    } catch (err) {
      setLoginError(err.message || 'Sign-in failed. Please try again.');
      setLoginLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    const check = checkRateLimitTiered(RATE_LIMIT_PRESETS.LANDING_PAGE_VIEW);
    if (!check.allowed) {
      setRateLimitMsg(`Too many requests. Try again in ${Math.ceil(check.retryAfterMs / 1000)} seconds.`);
      setLoading(false);
      return;
    }
    const fetchCheck = checkRateLimitTiered(RATE_LIMIT_PRESETS.LANDING_FETCH);
    if (!fetchCheck.allowed) {
      setRateLimitMsg(`Too many requests. Try again in ${Math.ceil(fetchCheck.retryAfterMs / 1000)} seconds.`);
      setLoading(false);
      return;
    }
    setRateLimitMsg('');
    readApi.getLandingData()
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((err) => {
        console.error('Error fetching landing data:', err);
        if (!cancelled) setData({ gameIds: [], gamesData: {} });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const games = useMemo(() => {
    if (!data?.gameIds?.length) return GAMES;
    return GAMES.filter((g) => data.gameIds.includes(g.id));
  }, [data?.gameIds]);

  const currentGameData = data?.gamesData?.[gameFilter] ?? { tournaments: [], matches: [] };
  const liveMatchIds = useMemo(() =>
    (currentGameData.matches || [])
      .filter((m) => m.status === 'in-progress')
      .map((m) => m.id)
      .slice(0, 10),
    [currentGameData.matches]
  );
  const liveMatches = useLiveMatches(liveMatchIds);

  const mergedMatches = useMemo(() => {
    const list = currentGameData.matches || [];
    return list.map((m) => (liveMatches[m.id] ? { ...m, ...liveMatches[m.id] } : m));
  }, [currentGameData.matches, liveMatches]);

  useEffect(() => {
    if (games.length > 0 && !games.some((g) => g.id === gameFilter)) {
      setGameFilter(games[0]?.id || 'table-tennis');
    }
  }, [games, gameFilter]);

  const gameLabel = getGameById(gameFilter)?.name || 'Table Tennis';

  function handleRegister(tournamentId) {
    startGoogleSignIn(`/tournament/register?tournamentId=${encodeURIComponent(tournamentId)}`);
  }

  return (
    <div className="landing-page">
      <header className="landing-header">
        <Link to="/" className="landing-brand" aria-label="GradedPlay - Home">
          <img src="/design/logos/gradedplay-high-resolution-logo-tranparent.png" alt="" className="landing-logo-img" aria-hidden />
        </Link>
        <nav className="landing-header-nav">
          <Link to="/privacy" className="landing-nav-link">Privacy</Link>
          <button
            type="button"
            className="landing-signin"
            onClick={() => startGoogleSignIn()}
            disabled={loginLoading}
          >
            {loginLoading ? 'Signing in…' : 'Sign in'}
          </button>
        </nav>
      </header>

      <main className="landing-main">
        <section className="landing-hero">
          <img src="/design/logos/gradedplay-high-resolution-logo.png" alt="" className="landing-hero-logo" aria-hidden />
          <h1>Sports made simple</h1>
          <p className="landing-hero-subtitle">
            Run tournaments, track scores, and build communities for table tennis, pickleball, badminton and more.
          </p>
          {loginError && <p className="landing-login-error-inline">{loginError}</p>}
          <div className="landing-hero-actions">
            <button type="button" className="btn-primary btn-hero" onClick={() => startGoogleSignIn()} disabled={loginLoading}>
              {loginLoading ? 'Signing in…' : 'Get started'}
            </button>
            <Link to="/privacy" className="landing-hero-link">Privacy &amp; Terms</Link>
          </div>
        </section>

        <section className="landing-scores">
          <h2 className="landing-section-title">Public tournament scores</h2>
          <p className="landing-section-desc">Recent results from open {gameLabel.toLowerCase()} tournaments.</p>

          {games.length > 1 && (
            <div className="landing-game-tabs">
              {games.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className={`landing-game-tab ${gameFilter === g.id ? 'active' : ''}`}
                  onClick={() => setGameFilter(g.id)}
                  style={{ '--game-accent': getGameAccent(g.id) }}
                >
                  {g.icon} {g.name}
                </button>
              ))}
            </div>
          )}

          {rateLimitMsg ? (
            <div className="landing-empty">
              <p className="landing-ratelimit">{rateLimitMsg}</p>
            </div>
          ) : loading ? (
            <LoadingBlock message="Loading…" skeletonRows={4} className="landing-loading-block" />
          ) : (
            <>
              {currentGameData.tournaments?.length > 0 && (
                <div className="landing-tournaments-section">
                  <h3 className="landing-subsection-title">Open tournaments</h3>
                  <ul className="landing-tournament-list">
                    {currentGameData.tournaments.slice(0, 8).map((t) => (
                      <li key={t.id} className="landing-tournament-card">
                        <div className="landing-tournament-info">
                          <h4>{t.name}</h4>
                          {t.description && <p className="landing-tournament-desc">{t.description}</p>}
                          <div className="landing-tournament-meta">
                            {t.startDate && <span>Start: {formatDate(t.startDate)}</span>}
                            {t.registrationsClosed && <span className="landing-badge">Registrations closed</span>}
                          </div>
                        </div>
                        <div className="landing-tournament-actions">
                          <button
                            type="button"
                            className="btn-primary btn-small"
                            onClick={() => handleRegister(t.id)}
                            disabled={t.registrationsClosed}
                          >
                            {t.registrationsClosed ? 'Closed' : 'Register'}
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="landing-scores-section">
                <h3 className="landing-subsection-title">Recent scores</h3>
                {mergedMatches.length === 0 ? (
                  <p className="landing-no-scores">No completed matches yet for {gameLabel.toLowerCase()}.</p>
                ) : (
                  <ul className="landing-scores-list">
                    {mergedMatches.slice(0, 15).map((m) => {
                      const { p1, p2 } = getPlayerLabel(m);
                      const isLive = m.status === 'in-progress';
                      return (
                        <li key={m.id} className={`landing-score-item ${isLive ? 'live' : ''}`}>
                          <Link to={`/sport/${gameFilter}/match/${m.id}`} className="landing-score-link">
                            <span className="landing-score-players">{p1} vs {p2}</span>
                            <span className="landing-score-result">
                              {isLive && <span className="landing-live-dot" />}
                              {formatScoreSummary(m.scores) || (isLive ? 'Live' : '—')}
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {(currentGameData.tournaments?.length === 0 && mergedMatches.length === 0) && (
                <div className="landing-empty">
                  <p>No open {gameLabel.toLowerCase()} tournaments at the moment.</p>
                  <p>Sign in to create tournaments or check back later.</p>
                  <button type="button" className="btn-primary" onClick={() => startGoogleSignIn()} disabled={loginLoading}>
                    {loginLoading ? 'Signing in…' : 'Sign in'}
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        <GoogleAd slotKey="BANNER" className="google-ad-banner" />

        <section className="landing-features">
          <h2 className="landing-section-title">What you can do</h2>
          <div className="landing-features-grid">
            {FEATURES.map((f) => (
              <div key={f.title} className="landing-feature-card">
                <span className="landing-feature-icon">{f.icon}</span>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <GoogleAd slotKey="BANNER" className="google-ad-rectangle" style={{ marginTop: '24px' }} />
      </main>

      <footer className="landing-footer">
        <p><Link to="/privacy">Privacy Policy &amp; Terms</Link></p>
      </footer>
    </div>
  );
}
